import angular from "angular";
import { pageNavigationItems, slotsThatFit } from "./page-navigation-items";

// <page-navigation page="ctrl.page" total-pages="ctrl.totalPages" on-page-change="ctrl.gotoPage(page)"></page-navigation>
//
// Bootstrap pagination that fills the available width on one row: first/last page always visible, […]
// buttons that jump a group at a time, and a "Page [ ] Go" box on the right to jump straight to any page.
// The number of page buttons is whatever fits the row and is recomputed when the element resizes.
// `buttons` (optional) fixes the number of page/[…] slots instead of fitting them to the width.
// Hidden when there is one page.
const DEFAULT_SLOTS = 11;

angular
    .module('ui-common')
    .directive('pageNavigation', function () {
        return {
            restrict: 'E',
            template: require('./page-navigation-template.html'),
            scope: {
                page: '<',
                totalPages: '<',
                buttons: '<?',
                onPageChange: '&'
            },
            link: function ($scope, elem) {
                const nav = elem[0].querySelector('.page-navigation');
                const list = elem[0].querySelector('.pagination');
                const gotoForm = elem[0].querySelector('.page-navigation-goto');
                let fittedSlots = DEFAULT_SLOTS;

                $scope.items = [];
                $scope.entered = { page: null, invalid: false };

                // Width of a button with each label, measured from hidden probe buttons appended to the row so
                // they carry the row's own styles. Zero when the row is not displayed.
                const measureButtonWidths = (labels) => {
                    const probes = labels.map(label => {
                        const item = document.createElement('li');
                        item.className = 'page-item';
                        item.style.position = 'absolute';
                        item.style.visibility = 'hidden';
                        const link = document.createElement('a');
                        link.className = 'page-link';
                        link.textContent = label;
                        item.appendChild(link);
                        list.appendChild(item);
                        return item;
                    });
                    const widths = probes.map(item => item.getBoundingClientRect().width);
                    for (const item of probes) {
                        item.remove();
                    }
                    return widths;
                };

                // How many page buttons fit beside « » and the "Page [ ] Go" box. Buttons differ in width by
                // digit count (a [1342] button is wider than a [12] button), so the row that each slot count
                // would produce for the current page is priced per button, using the width of a button with
                // that many digits, and the largest slot count whose row fits is used. On narrow pages the
                // CSS wraps the go box onto its own line, so it then takes no room from the buttons.
                const measuredSlots = () => {
                    const totalPages = Math.max(Math.floor($scope.totalPages) || 0, 1);
                    const digits = String(totalPages).length;
                    const labels = ['…', ...Array.from({ length: digits }, (_, i) => '0'.repeat(i + 1))];
                    const [gapWidth, ...digitWidths] = measureButtonWidths(labels);
                    const prev = list.firstElementChild.getBoundingClientRect().width;
                    const next = list.lastElementChild.getBoundingClientRect().width;
                    if (!gapWidth || !prev || !next || digitWidths.some(width => !width)) {
                        return null;
                    }
                    // Each width is the list item's, which is already a border narrower than its link: Bootstrap
                    // pulls every link after the first over the previous one with a negative margin.
                    const widthOf = (item) => item.gap ? gapWidth : digitWidths[String(item.page).length - 1];

                    const gap = parseFloat(getComputedStyle(nav).columnGap) || 0;
                    const form = gotoForm.getBoundingClientRect();
                    const formBesideButtons = form.width > 0 && form.top < list.getBoundingClientRect().bottom;
                    const reserved = formBesideButtons ? form.width + gap : 0;
                    const available = nav.clientWidth - reserved - prev - next;
                    return slotsThatFit($scope.page, totalPages, available, widthOf);
                };

                // `slots` is a fit already measured by the caller; otherwise the row is fitted here.
                const render = (slots) => {
                    if ($scope.buttons == null) {
                        // Fit before building the row so a page with other digit counts does not
                        // flash at the previous width and then re-render.
                        fittedSlots = slots ?? measuredSlots() ?? fittedSlots;
                    }
                    $scope.items = pageNavigationItems($scope.page, $scope.totalPages, $scope.buttons ?? fittedSlots);
                    // Every page has its own button, so there is nothing the go box could reach faster.
                    // It keeps its space (visibility, not display) so the row measurement stays stable.
                    $scope.showGoTo = $scope.items.some(item => item.gap);
                };
                $scope.$watchGroup(['page', 'totalPages', 'buttons'], () => render());

                const refit = () => {
                    if ($scope.buttons != null) {
                        return;
                    }
                    const slots = measuredSlots();
                    if (slots === null || slots === fittedSlots) {
                        return;
                    }
                    $scope.$applyAsync(() => render(slots));
                };

                if (typeof ResizeObserver === 'function') {
                    const observer = new ResizeObserver(refit);
                    observer.observe(nav);
                    $scope.$on('$destroy', () => observer.disconnect());
                } else {
                    angular.element(window).on('resize', refit);
                    $scope.$on('$destroy', () => angular.element(window).off('resize', refit));
                }
                // Refit after each render as well: the row may have just become visible, which a resize
                // observer on the nav does not always report.
                $scope.$watch('items', () => setTimeout(refit, 0));

                $scope.isValidTarget = (target) => {
                    return Number.isInteger(target) && target >= 1 && target <= $scope.totalPages && target !== $scope.page;
                };

                $scope.goTo = (target) => {
                    if (!$scope.isValidTarget(target)) {
                        return;
                    }
                    $scope.onPageChange({ page: target });
                };

                $scope.goToEntered = () => {
                    const target = Number($scope.entered.page);
                    const inRange = Number.isInteger(target) && target >= 1 && target <= $scope.totalPages;
                    $scope.entered.invalid = !inRange;
                    if (!inRange) {
                        return;
                    }
                    $scope.entered.page = null;
                    $scope.goTo(target);
                };

                $scope.enteredChanged = () => {
                    $scope.entered.invalid = false;
                };
            }
        };
    });
